/* 迁移'@/pages/intelligence/pages/TrendingGames/components/SearchSelect/index.tsx'组件并作修改
Changed the filtering, sorting methods, keyboard highlighting
 */

/* eslint-disable arrow-body-style */
/* eslint-disable @typescript-eslint/no-empty-interface */
import React, { useState, useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { useThrottleFn } from 'ahooks';
import { Button, DropdownBox, List, Popover, SearchBox } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import GameImage from '@home/components/GameImage';
import { getTextWithHighlight } from '@intelligence/utils/commonUtils';

import type { IBaseProps } from '@intelligence/types/common';
import type { ITrendingGame } from '@intelligence/pages/TrendingGames/types';

import './SearchSelect.scss';

interface ISearchSelectProps extends IBaseProps {
  options: ITrendingGame[];
  value: string;
  onSelect: (payload: ITrendingGame) => void;
}

const SearchSelect: React.FC<ISearchSelectProps> = (props) => {
  const { options, value, onSelect, className, style, selectedUidAndId } = props;

  const { formatMessage } = useIntl();

  const [visible, setVisible] = useState(false);
  const [keyword, setKeyword] = useState('');

  const filteredOptions = useMemo<ITrendingGame[]>(() => {
    const lowercasedKeyword = keyword.trim().toLowerCase();

    // 1. Filter options using a standard substring search
    const optionsTmp = lowercasedKeyword
      ? options.filter(option => option.name?.toLowerCase().includes(lowercasedKeyword))
      : options;

    // 2. Sort the filtered options 
    // (use [...optionTmp] to avoid mutating original options)
    return [...optionsTmp].sort((a, b) => {
      const aName = a?.name || '';
      const bName = b?.name || '';
      if (!aName && !bName) return 0;
      if (!aName) return 1;
      if (!bName) return -1;
      // Exact keyword match gets priority
      if (aName.toLowerCase() === lowercasedKeyword) return -1;
      if (bName.toLowerCase() === lowercasedKeyword) return 1;
      // Otherwise, sort alphabetically
      return aName.localeCompare(bName);
    });
  }, [options, keyword]);

  const isNoData = !(filteredOptions?.length > 0);

  const handleVisibleChange = (visible: boolean) => {
    if (visible) {
      setKeyword('');
    }
    setVisible(visible);
  };

  const handleSearch = (payload?: string) => {
    setKeyword(payload || '');
  };

  const { run: handleSearchRun } = useThrottleFn(handleSearch, { wait: 600 });

  const renderButtonLabel = () => {
    const length = options?.length || 0;
    const lengthText = length > 99 ? '99+' : length;
    // return `${formatMessage({ id: 'dbi_all' })} (${lengthText})`; // correct version (!! Do not remove)
    return `All (${lengthText})`; // temporary disable the CN version
  };

  return (
    <Popover
      trigger="click"
      placement="bottom-end"
      overlayClassName="dbs-trending-game-overlay"
      visible={visible}
      onVisibleChange={handleVisibleChange}
      closeOnScroll={true}
      overlay={
        <DropdownBox>
          <SearchBox
            className="dbs-trending-game-search"
            // placeholder={formatMessage({ id: 'dbi_search_any_game' })}  // correct version (!! Do not remove)
            placeholder={'Search any game'} // temporary disable the CN version
            onChange={handleSearchRun}
            onSearch={handleSearch}
          />
          {isNoData && (
            <div className="dbs-trending-game-list--no-data">
              {/* Temporary method to disable the CN version */}
              <div className="dbs-trending-game-list--no-data__inner">
                {/* {formatMessage({ id: 'dbi_search_empty' })} // correct version (!! Do not remove) */}
                No Data 
              </div>
            </div>
          )}
          {!isNoData && (
            <List type="option" className="dbs-select dbs-trending-game-list">
              {filteredOptions?.map((option, index) => (
                <List.Item
                  key={index}
                  selected={
                    selectedUidAndId
                      ? selectedUidAndId === option?.unified_id + option?.id
                      : value === option?.unified_id
                  }
                  onClick={() => {
                    onSelect(option);
                  }}
                >
                  <div className="dbs-trending-overlay-flex">
                    <GameImage
                      src={option?.cover}
                      className="dbs-trending-overlay-img"
                    />
                    <div className="dbs-trending-overlay-text">
                      {/* Highlight the keyword in the game name */}
                      {/* { isSeparate: false }  -> look for entire keyword as a single block */}
                      {getTextWithHighlight(option?.name, keyword, {
                        isSeparate: false,
                        className: 'dbs-add-entities-highlight',
                      })} 
                    </div>
                  </div>
                </List.Item>
              ))}
            </List>
          )}
        </DropdownBox>
      }
    >
      <Button
        style={style}
        className={classnames('dbs-trending-game-btn', className, {
          action: visible,
        })}
        type="weak"
        onClick={() => {
          handleVisibleChange(!visible);
        }}
      >
        {renderButtonLabel()}
        <SvgIcon type="arrow-down" className="dbs-trending-game-svg" />
      </Button>
    </Popover>
  );
};

export default SearchSelect;